import { type Bone, type BufferGeometry, Color, Float32BufferAttribute, type Material, type MeshPhongMaterial, type MeshStandardMaterial, type SkinnedMesh } from 'three'

/**
 * Tints the vertices skinned to a selected bone so the user can see which part of
 * the mesh that bone deforms. Drives the "highlight the rigged mesh" pose-mode UX.
 *
 * Approach: per-vertex colors via Three.js's standard `vertexColors` material flag.
 * To avoid the typical pitfall (per-vertex color multiplies into a non-white
 * material color, producing wrong tints), we snapshot the material's `color` on
 * `apply()`, override it to white, and encode the *original* color into the
 * per-vertex color buffer for unhighlighted vertices. Highlighted vertices get the
 * tint color directly. `dispose()` restores everything.
 *
 * Works with any Three material that exposes `color` (Phong/Standard/Basic/etc.).
 * Multi-mesh skinned models are handled — `apply` accepts the full mesh list.
 */

const DEFAULT_HIGHLIGHT_COLOR = new Color(0xff5555)
const WEIGHT_THRESHOLD = 0.001

interface MeshSnapshot {
  mesh: SkinnedMesh
  /** Per-material entries — a SkinnedMesh's `material` may be a single Material or an array. */
  material_snapshots: MaterialSnapshot[]
  /** The color attribute we added (or `null` if we replaced an existing one). */
  added_color_attribute: boolean
  /** The geometry we modified. */
  geometry: BufferGeometry
  /** Original color attribute, if one existed before `apply()`. Restored on dispose. */
  prior_color_attribute: Float32BufferAttribute | null
  /** Each vertex's "base color" (the original material color), used to repaint unhighlighted vertices. */
  base_color: Color
}

interface MaterialSnapshot {
  material: HasColor
  prior_color: Color
  prior_vertex_colors: boolean
}

type HasColor = Material & { color: Color, vertexColors?: boolean, needsUpdate?: boolean }

export class BoneWeightHighlighter {
  private snapshots: MeshSnapshot[] = []
  private highlight_color: Color = DEFAULT_HIGHLIGHT_COLOR

  /**
   * Switches the given meshes into highlight-ready state: materials become white +
   * vertexColors-enabled, and a per-vertex color attribute is attached to each
   * geometry, initialized to the prior material color. Idempotent — calling twice
   * with the same meshes returns immediately.
   */
  apply (skinned_meshes: SkinnedMesh[]): void {
    if (this.snapshots.length > 0) return // already applied

    for (const mesh of skinned_meshes) {
      const materials = this.get_color_materials(mesh)
      if (materials.length === 0) continue

      const material_snapshots: MaterialSnapshot[] = materials.map((material) => ({
        material,
        prior_color: material.color.clone(),
        prior_vertex_colors: material.vertexColors === true
      }))

      // Use the first material's color as the "base" — most models have one
      // material per mesh; multi-material meshes use the dominant one as the
      // baseline tint, which is good enough for highlighting.
      const base_color = materials[0].color.clone()

      const geometry = mesh.geometry
      const prior_color_attribute = geometry.getAttribute('color') as Float32BufferAttribute | undefined ?? null

      const vertex_count = geometry.getAttribute('position').count
      const colors = new Float32Array(vertex_count * 3)
      for (let i = 0; i < vertex_count; i++) {
        colors[i * 3] = base_color.r
        colors[i * 3 + 1] = base_color.g
        colors[i * 3 + 2] = base_color.b
      }
      geometry.setAttribute('color', new Float32BufferAttribute(colors, 3))

      // Override material.color to white so the per-vertex base_color reproduces
      // the original look exactly. Re-enable vertexColors so the shader actually
      // multiplies it in.
      for (const material of materials) {
        material.color.setRGB(1, 1, 1)
        material.vertexColors = true
        material.needsUpdate = true
      }

      this.snapshots.push({
        mesh,
        material_snapshots,
        added_color_attribute: prior_color_attribute === null,
        geometry,
        prior_color_attribute,
        base_color
      })
    }
  }

  /**
   * Repaint every snapshot's color attribute so that vertices weighted to `bone`
   * get `highlight_color` and others get `base_color`. Pass `null` to clear the
   * highlight (restores all vertices to base_color but leaves the highlighter
   * applied — `dispose()` is what fully unwires).
   */
  highlight_bone (bone: Bone | null): void {
    for (const snapshot of this.snapshots) {
      this.repaint_snapshot(snapshot, bone)
    }
  }

  /**
   * Restores material colors, removes (or restores) the per-vertex color attribute,
   * and clears internal state. Safe to call when nothing was applied.
   */
  dispose (): void {
    for (const snapshot of this.snapshots) {
      for (const ms of snapshot.material_snapshots) {
        ms.material.color.copy(ms.prior_color)
        ms.material.vertexColors = ms.prior_vertex_colors
        ms.material.needsUpdate = true
      }
      if (snapshot.added_color_attribute) {
        snapshot.geometry.deleteAttribute('color')
      } else if (snapshot.prior_color_attribute !== null) {
        snapshot.geometry.setAttribute('color', snapshot.prior_color_attribute)
      }
    }
    this.snapshots = []
  }

  set_highlight_color (color: Color): void {
    this.highlight_color = color.clone()
  }

  // -- Internal -------------------------------------------------------------

  private repaint_snapshot (snapshot: MeshSnapshot, bone: Bone | null): void {
    const geometry = snapshot.geometry
    const color_attr = geometry.getAttribute('color') as Float32BufferAttribute | undefined
    if (color_attr === undefined) return

    const colors = color_attr.array as Float32Array
    const vertex_count = color_attr.count

    const skin_index = geometry.getAttribute('skinIndex')
    const skin_weight = geometry.getAttribute('skinWeight')

    // Find the bone's index in this mesh's skeleton. Multi-mesh models with
    // separate skeletons may not contain `bone`, in which case nothing to highlight.
    const bone_index = bone === null ? -1 : snapshot.mesh.skeleton.bones.indexOf(bone)
    const should_highlight = bone !== null && bone_index !== -1 && skin_index !== undefined && skin_weight !== undefined

    const base = snapshot.base_color
    const tint = this.highlight_color

    for (let i = 0; i < vertex_count; i++) {
      let weight_for_bone = 0
      if (should_highlight) {
        for (let s = 0; s < 4; s++) {
          const idx = skin_index.getComponent(i, s)
          if (idx === bone_index) {
            const w = skin_weight.getComponent(i, s)
            if (w > weight_for_bone) weight_for_bone = w
          }
        }
      }

      if (weight_for_bone > WEIGHT_THRESHOLD) {
        // Blend in proportion to the weight so partially-weighted vertices
        // (skin falloff at joints) get a gradient tint instead of a hard edge.
        const w = Math.min(1, weight_for_bone)
        colors[i * 3] = base.r * (1 - w) + tint.r * w
        colors[i * 3 + 1] = base.g * (1 - w) + tint.g * w
        colors[i * 3 + 2] = base.b * (1 - w) + tint.b * w
      } else {
        colors[i * 3] = base.r
        colors[i * 3 + 1] = base.g
        colors[i * 3 + 2] = base.b
      }
    }

    color_attr.needsUpdate = true
  }

  private get_color_materials (mesh: SkinnedMesh): HasColor[] {
    const materials: HasColor[] = []
    const raw = mesh.material as Material | Material[] | undefined
    if (raw === undefined) return materials

    const candidates: Material[] = Array.isArray(raw) ? raw : [raw]
    for (const m of candidates) {
      const has_color = m as MeshPhongMaterial | MeshStandardMaterial
      if (typeof has_color.color === 'object' && has_color.color !== null && (has_color.color as Color).isColor === true) {
        materials.push(m as HasColor)
      }
    }
    return materials
  }
}
